import { useEffect, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Panel } from "./_shared";
import { FileText, Eye, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const SUP_ROLES = ["SUPER_ADMIN", "CTRL", "CTOP"];
const STAGES = [
  { key: "source_road", label: "Source Road" },
  { key: "air_leg", label: "Air Leg" },
  { key: "destination_road", label: "Destination Road" },
];

// M10A FE3 — READ-ONLY E-Way Bill presentation for SUPER_ADMIN / authorised CTRL / CTOP on their
// existing authorised shipment detail. Consumes ONLY the dedicated Inc-5 read routes
// (GET /shipments/{id}/eway-bills + the stage signed-url). Renders NO write controls (no upload,
// no replace, no number edit, no match action). Never shown to CAP / RID / Sender; Accounts uses its own
// dedicated workspace instead of this admin block.
export function SupervisorEwbPanel({ d, profile }) {
  const [rows, setRows] = useState(null);   // null = loading, [] = none
  const [err, setErr] = useState(false);
  const shipmentId = d.shipment.id;

  useEffect(() => {
    let alive = true;
    api.get(`/shipments/${shipmentId}/eway-bills`)
      .then((r) => { if (alive) setRows(r.data.eway_bills || []); })
      .catch(() => { if (alive) { setErr(true); setRows([]); } });
    return () => { alive = false; };
  }, [shipmentId]);

  if (!SUP_ROLES.includes(profile?.role)) return null;

  const invDocs = (d.documents || []).filter((x) => x.doc_type === "INV");
  const ewbByInvoice = {};
  (rows || []).forEach((e) => { ewbByInvoice[e.invoice_document_id] = e; });
  // Current invoices only (exclude those that are themselves a pending replacement).
  const currentInvoices = invDocs.filter((x) => !x.invoice_is_pending_replacement);

  const openStage = async (ewbId, stage) => {
    try {
      const { data } = await api.get(`/shipments/${shipmentId}/eway-bills/${ewbId}/stage/${stage}/signed-url`);
      if (data?.signed_url) window.open(data.signed_url, "_blank", "noopener");
      else toast.error("Document unavailable");
    } catch (e) { toast.error(e.displayMessage || "Could not open E-Way Bill document"); }
  };

  return (
    <Panel icon={<FileText size={16} weight="bold" />} title="E-WAY BILLS (READ-ONLY)">
      {rows === null ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="supervisor-ewb-loading">Loading…</p>
      ) : err ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="supervisor-ewb-error">Could not load E-Way Bill data.</p>
      ) : currentInvoices.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted))]" data-testid="supervisor-ewb-empty">E-Way Bill not yet available.</p>
      ) : (
        <div className="space-y-3" data-testid="supervisor-ewb-list">
          {currentInvoices.map((inv) => {
            const ewb = ewbByInvoice[inv.id];
            return (
              <div key={inv.id} className="border border-[hsl(var(--border))] rounded p-2"
                   data-testid={`supervisor-ewb-invoice-${inv.public_document_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="mono text-xs truncate">{inv.file_name || inv.public_document_id}</div>
                  {ewb ? (
                    <div className="text-xs mono">EWB No.: <span className="font-medium">{ewb.ewb_number || "— not set —"}</span></div>
                  ) : (
                    <div className="text-xs text-[hsl(var(--muted))]">E-Way Bill not yet available</div>
                  )}
                </div>
                {ewb && ewb.needs_accounts_check && (
                  <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1"
                       data-testid={`supervisor-ewb-pending-${inv.public_document_id}`}>
                    <WarningCircle size={12} weight="bold" /> Invoice replacement awaiting Accounts check
                  </div>
                )}
                {ewb && (
                  <div className="grid sm:grid-cols-3 gap-2 mt-2">
                    {STAGES.map((st) => {
                      const present = ewb.stages?.[st.key]?.present;
                      return (
                        <div key={st.key} className="text-xs" data-testid={`supervisor-ewb-stage-${st.key}-${inv.public_document_id}`}>
                          <div className="mono font-medium">{st.label}</div>
                          {present ? (
                            <button className="btn-ghost text-xs mt-0.5" onClick={() => openStage(ewb.id, st.key)}
                                     data-testid={`supervisor-ewb-view-${st.key}-${inv.public_document_id}`}>
                              <Eye size={12} weight="bold" /> View
                            </button>
                          ) : (
                            <div className="text-[hsl(var(--muted))] mt-0.5">No document</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
